import React, { PureComponent } from 'react';
import { CancelToken } from 'axios';
import { Helmet } from 'react-helmet';
import { parse } from 'qs';
import { WidgetMunicipal48h } from '../../widgets/Municipal48h/WidgetMunicipal48h';
import API from '../../utils/api';
import {
    getLocationData,
    celsiusToFahrenheit,
    kmToKnots,
    validateColor,
    consoleLogError,
    formatDateUi,
} from '../../utils/utils';
import {
    EST_CEL,
    TEMP,
    TEMP_XAF,
    PREC_ACU,
    HUM_REL,
    VENT_DIR,
    VENT_VELO,
} from '../../utils/variables';
import { translate } from '../../utils/translations';
import PropTypes from 'prop-types';
import { DEFAULT_CONFIG, WIDGETS_PADDING, SUFFIXES } from '../../utils/globals';
import { DateTime } from 'luxon';

class Municipal48h extends PureComponent {
    state = {
        config: buildMunicipal48hConfig({}),
        data: {},
        cities: [],
        widgetDate: '',
    };

    static propTypes = {
        preview: PropTypes.bool,
        color: PropTypes.string,
        language: PropTypes.string,
        alternativeTitle: PropTypes.string,
        windSpeedFormat: PropTypes.string,
        tempFormat: PropTypes.string,
        mainChart: PropTypes.string,
        secondaryChart: PropTypes.bool,
    };

    render() {
        return (
            <div className="Municipal48h" style={WIDGETS_PADDING}>
                <Helmet>
                    <title>
                        {translate(
                            this.state.config.language,
                            'municipal_48h_title'
                        )}
                    </title>
                </Helmet>
                <WidgetMunicipal48h
                    config={this.state.config}
                    data={this.state.data}
                    cities={this.state.cities}
                    mainChart={this.props.mainChart}
                    secondaryChart={this.props.secondaryChart}
                    widgetDate={this.state.widgetDate}
                />
            </div>
        );
    }

    componentDidMount() {
        this.feedWidget();
    }

    componentDidUpdate(prevProps) {
        if (this.props !== prevProps) {
            this.feedWidget();
        }
    }

    feedWidget = () => {
        const search = this.props.location ? this.props.location.search : '';
        let config = {};

        // From Configurador form
        if (this.props.preview === true) {
            const {
                alternativeTitle,
                color,
                language,
                location,
                tempFormat,
                windSpeedFormat,
                mainChart,
                secondaryChart,
            } = this.props;

            config = {
                alternativeTitle,
                color,
                language,
                location,
                variables: [
                    EST_CEL,
                    TEMP,
                    TEMP_XAF,
                    PREC_ACU,
                    HUM_REL,
                    VENT_DIR,
                    VENT_VELO,
                ],
                tempFormat,
                windSpeedFormat,
                mainChart,
                secondaryChart,
            };
        } else {
            config = buildMunicipal48hConfig(
                parse(search, {
                    ignoreQueryPrefix: true,
                })
            );
        }
        this.cancelTokenSource = CancelToken.source();

        this.setState({ config });

        this.getData(config);
        this.getCities(config);
    };

    formatData = data => {
        let i = 0;
        let outputData = {};

        // Widget Date
        const widgetDate = formatDateUi(new Date(data[0].data.dataSortida));
        this.setState({ widgetDate });

        this.state.config.variables.forEach(el => {
            outputData[el] = {};
            outputData[el]['1'] = getLocationData(
                this.state.config.location,
                data[i].data
            );
            outputData[el]['2'] = getLocationData(
                this.state.config.location,
                data[i + 1].data
            );

            i += 2;
        });

        return outputData;
    };

    getData = async config => {
        try {
            let data = await API.getMunicipal48h(
                config,
                this.cancelTokenSource.token
            );

            data = this.formatData(data);

            Object.keys(data).forEach(variable => {
                // Winter time or Summer time
                const offsetUTC =
                    DateTime.fromISO(data[variable]['1']['0'].data).offset / 60;

                // UTC to local
                data[variable]['1'].forEach(hour => {
                    hour.data = DateTime.fromISO(hour.data).toString();
                });
                data[variable]['2'].forEach(hour => {
                    hour.data = DateTime.fromISO(hour.data).toString();
                });

                // Rebuilding arrays
                const slices = { start: 0, end: 24 - offsetUTC };
                const slicesNextDay = { start: 24 - offsetUTC, end: 24 };

                const dataVariable1 = [...data[variable]['1']].slice(
                    slices.start,
                    slices.end
                );
                const firstHoursNextDay = [...data[variable]['1']].slice(
                    slicesNextDay.start,
                    slicesNextDay.end
                );
                const dataVariable2 = [...data[variable]['2']].slice(
                    slices.start,
                    slices.end
                );

                // TODO les dues primeres hores d'avui amb el JSON d'ahir
                data[variable]['1'] =
                    offsetUTC === 1
                        ? [{ valor: null, data: '' }, ...dataVariable1]
                        : [
                              { valor: null, data: '' },
                              { valor: null, data: '' },
                              ...dataVariable1,
                          ];

                data[variable]['2'] = [...firstHoursNextDay, ...dataVariable2];
            });

            // Transform to Fahrenheit if needed
            if (this.props.tempFormat === SUFFIXES.temperature.fahrenheit) {
                data.temp[1].forEach(
                    temp => (temp.valor = celsiusToFahrenheit(temp.valor))
                );
                data.temp[2].forEach(
                    temp => (temp.valor = celsiusToFahrenheit(temp.valor))
                );
                data.temp_xaf[1].forEach(
                    temp_xaf =>
                        (temp_xaf.valor = celsiusToFahrenheit(temp_xaf.valor))
                );
                data.temp_xaf[2].forEach(
                    temp_xaf =>
                        (temp_xaf.valor = celsiusToFahrenheit(temp_xaf.valor))
                );
            }

            // Transform to Knots if needed
            if (this.props.windSpeedFormat === SUFFIXES.speed.knots) {
                data.vent_velo[1].forEach(
                    vent_velo => (vent_velo.valor = kmToKnots(vent_velo.valor))
                );
                data.vent_velo[2].forEach(
                    vent_velo => (vent_velo.valor = kmToKnots(vent_velo.valor))
                );
            }

            this.setState({ data });
        } catch (e) {
            consoleLogError(e, this.state.config.language);
        }
    };

    getCities = async config => {
        this.cancelTokenSource = CancelToken.source();
        try {
            let cities = await API.getMunicipis(this.cancelTokenSource.token);
            this.setState({ cities: cities.data });
        } catch (e) {
            consoleLogError(e, this.state.config.language);
        }
    };

    componentWillUnmount() {
        this.cancelTokenSource.cancel(
            translate(this.state.config.language, 'cancelled_request')
        );
    }
}

const buildMunicipal48hConfig = ({
    location = DEFAULT_CONFIG.location,
    alternativeTitle = '',
    language = DEFAULT_CONFIG.language,
    color = '',
    variables = [
        EST_CEL,
        TEMP,
        TEMP_XAF,
        PREC_ACU,
        HUM_REL,
        VENT_DIR,
        VENT_VELO,
    ],
    mainChart = DEFAULT_CONFIG.mainChart,
    secondaryChart = DEFAULT_CONFIG.secondaryChart,
    tempFormat = DEFAULT_CONFIG.tempFormat,
    windSpeedFormat = DEFAULT_CONFIG.windSpeedFormat,
}) => {
    color = `#${color}`;
    if (validateColor(color) === false) color = DEFAULT_CONFIG.color;
    secondaryChart = secondaryChart === 'true' ? true : false;
    return {
        location,
        alternativeTitle,
        language,
        color,
        variables,
        mainChart,
        secondaryChart,
        tempFormat,
        windSpeedFormat,
    };
};

export default Municipal48h;



// WEBPACK FOOTER //
// ./src/containers/Municipal48h/Municipal48h.js